import { getUserLoginCookie, removeUserLoginCookie } from "api/common/cookie";
import { getDeviceMeta, getOrCreateDeviceId } from "api/common/device";
import { downloadBlob } from "api/common/files";
import getConst from "api/common/getConst";
import { redirect } from "api/common/links";

import type { IPaginationData } from "api/interfaces/data/IPaginationData";
import type { IWebApiResult } from "api/interfaces/data/IWebApiResult";
import lang from "lang";

interface IDataProviderExtraProps {
    withoutInterceptors?: boolean;
    responseType?: "json" | "blob" | "string";
    requestType?: "json" | "params" | "raw";
    headerPagination?: boolean;
    withFileName?: boolean;
    headers?: Headers;
}
export interface IDataProviderErrorResponse {
    status: number;
    statusText: string;
    data?: any;
}

export const webApiResultError = (message: string) => {
    return {
        name: "webApiResultError",
        message: message,
    };
};

interface ICheckDataResult<T> {
    error?: {
        name: string;
        message: string;
    };
    result: T | null;
    pagination?: IPaginationData;
}

export interface IResponseWithFileName extends Response {
    fileName?: string;
}

/**
 * Скачивает документ по результату запроса dataProvider (обычно с withFileName: true).
 * @param request Promise от dataProvider
 * @param defaultFileName Имя файла, если сервер не вернул content-disposition
 */
export const downloadDocument = async (
    request: Promise<IResponseWithFileName>,
    defaultFileName: string = "download.xlsx"
): Promise<void> => {
    const res = await request;
    let fileName = defaultFileName;
    if (res.fileName) {
        fileName = res.fileName;
    }
    const blob = await res.blob();
    downloadBlob(blob, fileName);
};

export const webApiResultData = <T>(data: IWebApiResult<T>): ICheckDataResult<T> => {
    if (data?.hasError) {
        return {
            error: webApiResultError(data?.message || "error"),
            result: null,
        };
    }
    const result: ICheckDataResult<T> = { result: data.responseObject };
    if (data?.paginationData) {
        result.pagination = data.paginationData;
    }
    return result;
};
export type TCRUDDataProviderAction = "getAll" | "getRecord" | "save" | "remove";
export class CRUDDataProvider<T> {
    private baseUrl: string;
    private softDelete: boolean;
    constructor(baseUrl: string, softDelete: boolean = true) {
        this.baseUrl = baseUrl;
        this.softDelete = softDelete;
    }
    getData(actions: TCRUDDataProviderAction[] = ["getAll", "getRecord", "remove", "save"]) {
        return {
            getAll: (): Promise<IWebApiResult<T[]>> => {
                if (!actions.includes("getAll")) {
                    return new Promise<IWebApiResult<T[]>>((_, reject) =>
                        reject(webApiResultError("method not included"))
                    );
                }
                return dataProvider(this.baseUrl);
            },
            getRecord: (id: number): Promise<IWebApiResult<T>> => {
                if (!actions.includes("getRecord")) {
                    return new Promise<IWebApiResult<T>>((_, reject) =>
                        reject(webApiResultError("method not included"))
                    );
                }
                return dataProvider(`${this.baseUrl}/${id}`, "get");
            },
            save: (data: T): Promise<IWebApiResult<boolean>> => {
                if (!actions.includes("save")) {
                    return new Promise<IWebApiResult<boolean>>((_, reject) =>
                        reject(webApiResultError("method not included"))
                    );
                }
                return dataProvider(this.baseUrl, "put", data);
            },
            remove: (ids: number[]): Promise<IWebApiResult<boolean>> => {
                if (!actions.includes("remove")) {
                    return new Promise<IWebApiResult<boolean>>((_, reject) =>
                        reject(webApiResultError("method not included"))
                    );
                }
                let url = this.baseUrl;
                if (!this.softDelete) {
                    url += "/false";
                }
                return dataProvider(url, "delete", ids);
            },
        };
    }
}

export const dataProvider = async (
    url: string,
    method: "get" | "post" | "put" | "delete" | "patch" = "get",
    data?: any,
    extraProps: IDataProviderExtraProps = {}
) => {
    const defaultExtraProps: IDataProviderExtraProps = {
        requestType: "json",
        responseType: "json",
        withoutInterceptors: false,
        headers: undefined,
    };
    extraProps = { ...defaultExtraProps, ...extraProps };
    url = getConst("api-url-v2") + url;
    const headersParams = getHeadersProvider(extraProps);
    const params: any = {
        method: method,
        headers: headersParams,
    };
    if (method !== "get" && typeof data != "undefined") {
        switch (extraProps.requestType) {
            case "json":
                params.body = JSON.stringify(data);
                break;
            case "params":
                {
                    const p = new URLSearchParams();
                    Object.keys(data).forEach((key) => p.append(key, data[key]));
                    params.body = p.toString();
                }
                break;
            case "raw":
                params.body = data;
                break;
        }
    } else {
        if (data !== "undefined") {
            if (typeof data === "object" && data !== null) {
                const paramsString = new URLSearchParams(data).toString();
                if (paramsString) {
                    url += "?" + paramsString;
                }
            }
        }
    }
    return fetch(url, params)
        .then(async (response) => {
            const isJson = response.headers.get("content-type")?.includes("application/json");
            const data = isJson ? await response.json() : response;
            if (!response.ok) {
                const error: IDataProviderErrorResponse = {
                    status: response.status,
                    statusText: response.statusText,
                    data: data,
                };
                return Promise.reject(error);
            }
            if (!!extraProps?.withFileName && response.headers.has("content-disposition")) {
                const fileName = getFilenameFromContentDisposition(
                    response.headers.get("content-disposition")
                );
                data.fileName = fileName;
            }
            if (!!extraProps?.headerPagination && response.headers.has("x-pagination")) {
                const paginationString = response.headers.get("x-pagination");
                if (typeof paginationString === "string") {
                    try {
                        const { pageNumber, pageSize, totalItemCount } =
                            JSON.parse(paginationString);
                        data.paginationData = {
                            pageNumber,
                            pageSize,
                            totalItemCount,
                        } as IPaginationData;
                    } catch (err) {
                        //
                    }
                }
            }
            if (extraProps.responseType === "string") {
                const stringData = await data.text();
                return stringData;
            }
            return data;
        })
        .catch((err) => {
            if (typeof err?.status !== "undefined") {
                switch (err.status) {
                    case 401:
                        toLogout();
                        return;
                    case 403:
                        if (err?.statusText === "Forbidden") {
                            err = webApiResultError(lang.system.data.errors.forbidden);
                        }

                        break;
                }
            }
            throw err;
        });
};
export const getHeadersProvider = (extraProps: IDataProviderExtraProps): Headers => {
    let params: any = {};
    if (!extraProps.withoutInterceptors) {
        const token = getUserLoginCookie();
        if (token) {
            params["Authorization"] = `Bearer ${token}`;
            params["X-CM-Client"] = getConst("app-version");
        } else {
            toLogout();
        }
    }
    if (extraProps.requestType === "json") {
        params["Content-Type"] = "application/json; charset=utf-8";
    }
    if (!!extraProps.headers && typeof extraProps == "object") {
        params = { ...params, ...extraProps.headers };
    }

    try {
        const deviceId = getOrCreateDeviceId();
        const { platform, deviceName } = getDeviceMeta();
        params["X-Device-Id"] = deviceId;
        params["X-Device-Name"] = deviceName;
        params["X-Device-Platform"] = platform;
    } catch {
        //
    }

    return new Headers(params);
};

function toLogout() {
    removeUserLoginCookie();
    window.sessionStorage.clear();
    // Сохраняем биометрические данные перед очисткой
    const biometricEnabled = localStorage.getItem("biometric_enabled");
    const biometricCredentialId = localStorage.getItem("biometric_credential_id");
    const lastBiometricPhone = localStorage.getItem("last_biometric_phone");
    const biometricDontAskAgain = localStorage.getItem("biometric_dont_ask_again");

    window.localStorage.clear();

    // Восстанавливаем биометрические данные (номер телефона очищаем от "+")
    if (biometricEnabled) localStorage.setItem("biometric_enabled", biometricEnabled);
    if (biometricCredentialId)
        localStorage.setItem("biometric_credential_id", biometricCredentialId);
    if (lastBiometricPhone) {
        const cleanPhone = lastBiometricPhone.replace(/^\+/, "");
        localStorage.setItem("last_biometric_phone", cleanPhone);
    }
    if (biometricDontAskAgain)
        localStorage.setItem("biometric_dont_ask_again", biometricDontAskAgain);

    redirect("/");
}
function getFilenameFromContentDisposition(contentDisposition?: string | null): string {
    if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (filenameStarMatch) {
            return decodeURIComponent(filenameStarMatch[1]);
        }

        // Если filename* не найден, ищем обычный filename
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
        if (filenameMatch) {
            // Убираем кавычки, если они есть
            return filenameMatch[1].replace(/["']/g, "");
        }
    }
    return "";
}
